/**
 * Modal Component
 * 
 * A reusable modal dialog component that can be used to display content in a popup.
 * Features:
 * - Customizable header, content, and footer
 * - Backdrop click to close
 * - Escape key to close
 * - Focus trap for accessibility
 * - Proper ARIA attributes
 * - Animation support
 * - Uses React Portal to render outside the normal DOM hierarchy
 * 
 * @module Modal
 */

import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

/**
 * Modal component for displaying content in a popup dialog
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @param {string} props.title - Title to display in the modal header
 * @param {React.ReactNode} props.children - Content to display in the modal
 * @param {string} props.size - Size of the modal ('small', 'medium', 'large', 'fullscreen')
 * @param {boolean} props.closeOnBackdropClick - Whether to close the modal when clicking the backdrop
 * @param {boolean} props.showCloseButton - Whether to show the close button in the header
 * @param {React.ReactNode} props.footer - Custom footer content
 * @returns {JSX.Element|null} Modal component or null if not open
 */
function Modal({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  size = 'medium',
  closeOnBackdropClick = true,
  showCloseButton = true,
  footer
}) {
  const modalRef = useRef(null);
  const firstFocusableElementRef = useRef(null);
  const lastFocusableElementRef = useRef(null);

  // Handle escape key press to close the modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }

      // Trap focus within the modal for accessibility
      if (e.key === 'Tab' && isOpen) {
        // If shift + tab and focus is on first element, move to last element
        if (e.shiftKey && document.activeElement === firstFocusableElementRef.current) {
          e.preventDefault();
          lastFocusableElementRef.current?.focus();
        }
        // If tab and focus is on last element, move to first element
        else if (!e.shiftKey && document.activeElement === lastFocusableElementRef.current) {
          e.preventDefault();
          firstFocusableElementRef.current?.focus();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      
      // Set focus to the modal
      setTimeout(() => {
        firstFocusableElementRef.current?.focus();
      }, 50);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Set up focus trap refs when modal content changes
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length > 0) {
        firstFocusableElementRef.current = focusableElements[0];
        lastFocusableElementRef.current = focusableElements[focusableElements.length - 1];
      }
    }
  }, [isOpen, children]);

  // Ensure the modal-root container exists
  useEffect(() => {
    // Check if modal-root already exists
    let modalRoot = document.getElementById('modal-root');
    
    // Create it if it doesn't exist
    if (!modalRoot) {
      modalRoot = document.createElement('div');
      modalRoot.id = 'modal-root';
      document.body.appendChild(modalRoot);
      
      // Apply styles to ensure it appears on top of everything
      modalRoot.style.position = 'fixed';
      modalRoot.style.top = '0';
      modalRoot.style.left = '0';
      modalRoot.style.width = '0';
      modalRoot.style.height = '0';
      modalRoot.style.overflow = 'visible';
      modalRoot.style.zIndex = '10000'; // Very high z-index
    }
  }, []);
  
  // Manage body scroll
  useEffect(() => {
    if (isOpen) {
      // Prevent scrolling of the body when modal is open
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // Don't render anything if the modal is not open
  if (!isOpen) return null;

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  // Get the modal-root element or fall back to body if not available yet
  const modalRoot = document.getElementById('modal-root') || document.body;
  
  // Use ReactDOM.createPortal to render the modal outside the normal DOM hierarchy
  return ReactDOM.createPortal(
    <div className="modal-backdrop" onClick={handleBackdropClick} data-testid="modal-backdrop">
      <div 
        ref={modalRef}
        className={`modal-container modal-${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        data-testid="modal-container"
        style={{ 
          zIndex: 10001, // Ensure this is higher than the modal-root container
          position: 'relative' 
        }}
      >
        <div className="modal-header">
          <h2 id="modal-title" className="modal-title">{title}</h2>
          {showCloseButton && (
            <button 
              className="modal-close-button" 
              onClick={onClose}
              aria-label="Close modal"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          )}
        </div>
        
        <div className="modal-content">
          {children}
        </div>
        
        {footer && (
          <div className="modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>,
    modalRoot
  );
}

export default Modal;
